import { getAssetUrl } from "helpers/assetHelpers";
import { getMediaMimeTypeFromUrl } from "helpers/getMediaMimeTypeFromUrl";

export const getPreviewUrl = async (url: string) => {
  const type = getMediaMimeTypeFromUrl(url);

  if (type?.kind === "image") {
    return getAssetUrl(url);
  }

  const assetUrl = getAssetUrl(url);
  return await PreviewFrameExtractor.getFirstFrame(assetUrl);
};

const getFirstFrameDataURL = (videoUrl: string): Promise<string> => {
  return new Promise((resolve) => {
    const video = document.createElement("video");

    video.crossOrigin = "anonymous";
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.style.display = "none";

    document.body.appendChild(video);

    const fail = () => {
      cleanup();
      resolve("");
    };

    const cleanup = () => {
      video.pause();
      video.remove();
    };

    // Safari prefers canplay over loadeddata
    video.addEventListener("canplay", async () => {
      // Safari needs a small delay before seeking
      await new Promise((r) => setTimeout(r, 50));

      try {
        video.currentTime = 0;
      } catch {
        return fail();
      }
    });

    video.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return fail();

        ctx.drawImage(video, 0, 0);
        const data = canvas.toDataURL("image/png");

        cleanup();
        resolve(data);
      } catch {
        fail();
      }
    });

    video.addEventListener("error", fail);

    video.src = videoUrl;
  });
};

class PreviewFrameExtractor {
  private static cache = new Map<string, string>(); // url → dataURL

  static async getFirstFrame(url: string): Promise<string> {
    if (this.cache.has(url)) {
      return this.cache.get(url)!;
    }

    const dataUrl = await getFirstFrameDataURL(url);

    // Only store if successfully extracted
    if (dataUrl) {
      this.cache.set(url, dataUrl);
    }

    return dataUrl;
  }
}
